import React, { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'design_system/src';
import { ReactComponent as Idea } from 'design_system/src/static/icons/idea.svg';
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	TooltipProps,
} from 'recharts';
import { LightTooltip } from 'components/hocs/tooltip/Tooltip';
import { fetchDashboardStats } from 'models/dashboard/controllers/dashboard.controller';
import {
	ActivityItem,
	DashboardStats,
	DailyMetric,
	PeriodMetric,
} from 'models/dashboard/interfaces/dashboard.interface';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { extractErrorMessage } from 'models/shared/helpers/errors';
import { getUser } from 'localStorage/authStorage';
import {
	ACTIVITY_DOT_COLORS,
	formatChartDate,
	formatFullDate,
	formatRelativeTime,
	getGreeting,
	SPARKLINE_COLORS,
} from './dashboardHelpers';

import './dashboard.scss';

const METRIC_TOOLTIPS = {
	totalTagsInstalled: 'Vehículos activos con tag asignado en tu cuenta.',
	totalTagsDelivered: 'Total de tags entregados a tu cuenta.',
	averageInstallationsPerMonth:
		'Total de instalaciones ÷ meses distintos en los que hubo al menos 1 instalación.',
	totalConversations: 'Número de conversaciones con SauronAI en tu cuenta.',
	activeGeofences: 'Geocercas con activas en tu cuenta.',
	inactiveVehicles: 'Vehículos con estado inactivo en tu cuenta.',
} as const;

type TrendResult =
	| { direction: 'up' | 'down' | 'flat'; percent: number }
	| { direction: 'none' };

function computeTrend(data: number[]): TrendResult {
	if (!data.length || data.every((v) => v === 0)) return { direction: 'none' };
	const first = data[0];
	const last = data[data.length - 1];
	if (first === 0) return { direction: last > 0 ? 'up' : 'flat', percent: 100 };
	const percent = Math.round(((last - first) / Math.abs(first)) * 100);
	if (percent > 0) return { direction: 'up', percent };
	if (percent < 0) return { direction: 'down', percent: Math.abs(percent) };
	return { direction: 'flat', percent: 0 };
}

function TrendBadge({ data }: { data: number[] }) {
	const trend = computeTrend(data);
	if (trend.direction === 'none') return null;
	if (trend.direction === 'flat') {
		return <span className="dashboard_kpi_trend flat">→ Sin cambios</span>;
	}
	const arrow = trend.direction === 'up' ? '↑' : '↓';
	return (
		<span className={`dashboard_kpi_trend ${trend.direction}`}>
			{arrow} {trend.percent}%
		</span>
	);
}

type KpiColor = 'orange' | 'blue' | 'green' | 'purple' | 'amber' | 'red';
type SparklineKey = keyof DashboardStats['kpiSparklines'];

interface KpiCardProps {
	title: string;
	value: number | string;
	subtitle?: string;
	tooltip: string;
	icon: React.ReactNode;
	color: KpiColor;
	sparklineData: number[];
}

type KpiCardConfig = Omit<KpiCardProps, 'sparklineData'> & {
	sparklineKey: SparklineKey;
};

interface ChartPoint {
	date: string;
	label: string;
	installed: number;
	delivered: number;
}

function InfoIcon({ tooltip }: { tooltip: string }) {
	return (
		<LightTooltip title={tooltip} placement="top" arrow enterDelay={200}>
			<Idea className="dashboard_info_icon" aria-label="Más información" />
		</LightTooltip>
	);
}

function Sparkline({ data, color }: { data: number[]; color: KpiColor }) {
	const points = data.length === 7 ? data : Array(7).fill(0);
	const allZero = points.every((v) => v === 0);
	const stroke = allZero ? SPARKLINE_COLORS.gray : SPARKLINE_COLORS[color];
	const width = 100;
	const height = 22;
	const max = Math.max(...points, 1);
	const min = Math.min(...points, 0);
	const range = max - min || 1;

	const polylinePoints = points
		.map((value, index) => {
			const x =
				points.length === 1 ? width / 2 : (index / (points.length - 1)) * width;
			const y = height - ((value - min) / range) * (height - 4) - 2;
			return `${x},${y}`;
		})
		.join(' ');

	return (
		<svg
			className="dashboard_sparkline"
			viewBox={`0 0 ${width} ${height}`}
			preserveAspectRatio="none"
			aria-hidden="true"
		>
			<polyline
				points={polylinePoints}
				fill="none"
				stroke={stroke}
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function KpiCard({
	title,
	value,
	subtitle,
	tooltip,
	icon,
	color,
	sparklineData,
}: KpiCardProps) {
	return (
		<div className="dashboard_kpi_card">
			<div className={`dashboard_kpi_icon ${color}`}>{icon}</div>
			<div className="dashboard_kpi_value">{value}</div>
			<Sparkline data={sparklineData} color={color} />
			<div className="dashboard_kpi_label">
				{title}
				<InfoIcon tooltip={tooltip} />
			</div>
			<TrendBadge data={sparklineData} />
			{subtitle && <div className="dashboard_kpi_subtitle">{subtitle}</div>}
		</div>
	);
}

function ChartTooltip({
	active,
	payload,
	label,
}: TooltipProps<number, string>) {
	if (!active || !payload?.length) return null;

	return (
		<div className="dashboard_chart_tooltip">
			<div className="dashboard_chart_tooltip_date">{label}</div>
			{payload.map((entry) => (
				<div key={entry.dataKey} className="dashboard_chart_tooltip_row">
					<span
						className="dashboard_chart_tooltip_dot"
						style={{ background: entry.color }}
					/>
					<span>{entry.name}:</span>
					<strong>{entry.value}</strong>
				</div>
			))}
		</div>
	);
}

function InstallationsChart({ data }: { data: DailyMetric[] }) {
	const chartData: ChartPoint[] = useMemo(
		() =>
			data.map((point) => ({
				...point,
				label: formatChartDate(point.date),
			})),
		[data],
	);

	return (
		<div className="dashboard_card dashboard_chart_card">
			<div className="dashboard_card_header">
				<span className="dashboard_card_title">Instalaciones por periodo</span>
				<div className="dashboard_chart_legend">
					<span className="dashboard_chart_legend_item installed">
						Tags instalados
					</span>
					<span className="dashboard_chart_legend_item delivered">
						Tags entregados
					</span>
				</div>
			</div>
			<div className="dashboard_chart_container">
				<ResponsiveContainer width="100%" height={260}>
					<LineChart
						data={chartData}
						margin={{ top: 8, right: 12, left: -16, bottom: 0 }}
					>
						<CartesianGrid stroke="#F3F3F1" vertical={false} />
						<XAxis
							dataKey="label"
							tick={{ fontSize: 11, fill: '#999' }}
							tickLine={false}
							axisLine={{ stroke: '#eee' }}
							interval="preserveStartEnd"
						/>
						<YAxis
							allowDecimals={false}
							tick={{ fontSize: 11, fill: '#999' }}
							tickLine={false}
							axisLine={false}
							width={32}
						/>
						<Tooltip content={<ChartTooltip />} />
						<Line
							type="monotone"
							dataKey="installed"
							name="Tags instalados"
							stroke="#FF5C00"
							strokeWidth={2}
							dot={false}
							activeDot={{ r: 4 }}
						/>
						<Line
							type="monotone"
							dataKey="delivered"
							name="Tags entregados"
							stroke="#2563EB"
							strokeWidth={2}
							dot={false}
							activeDot={{ r: 4 }}
						/>
					</LineChart>
				</ResponsiveContainer>
			</div>
		</div>
	);
}

function PeriodTable({
	installed,
	delivered,
}: {
	installed: PeriodMetric;
	delivered: PeriodMetric;
}) {
	const rows = [
		{
			label: 'Tags instalados',
			icon: <MemoryOutlinedIcon fontSize="inherit" />,
			color: '#FF5C00',
			values: [installed.lastWeek, installed.lastTwoWeeks, installed.lastMonth],
		},
		{
			label: 'Tags entregados',
			icon: <Inventory2OutlinedIcon fontSize="inherit" />,
			color: '#2563EB',
			values: [delivered.lastWeek, delivered.lastTwoWeeks, delivered.lastMonth],
		},
	];

	return (
		<div className="dashboard_card dashboard_period_card">
			<div className="dashboard_card_header">
				<span className="dashboard_card_title">Actividad por periodo</span>
			</div>
			<table className="dashboard_period_table">
				<thead>
					<tr>
						<th aria-label="Métrica" />
						<th>Última semana</th>
						<th>Últimas 2 semanas</th>
						<th>Último mes</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<tr key={row.label}>
							<td className="dashboard_period_row_label">
								<span
									className="dashboard_period_row_icon"
									style={{ color: row.color }}
								>
									{row.icon}
								</span>
								{row.label}
							</td>
							{row.values.map((v, i) => (
								<td key={i} className="dashboard_period_value">
									{v}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function ActivityPanel({ activities }: { activities: ActivityItem[] }) {
	return (
		<div className="dashboard_card dashboard_activity_card">
			<div className="dashboard_card_header">
				<span className="dashboard_card_title">Actividad reciente</span>
			</div>
			{activities.length === 0 ? (
				<div className="dashboard_activity_empty">
					<HistoryOutlinedIcon className="dashboard_activity_empty_icon" />
					<p>Sin actividad reciente</p>
				</div>
			) : (
				<div className="dashboard_activity_list">
					{activities.map((item) => (
						<div
							key={`${item.type}-${item.timestamp}-${item.text}`}
							className="dashboard_activity_item"
						>
							<span
								className={`dashboard_activity_dot ${item.type}`}
								style={{ background: ACTIVITY_DOT_COLORS[item.type] }}
							/>
							<span className="dashboard_activity_text">{item.text}</span>
							<span className="dashboard_activity_time">
								{formatRelativeTime(item.timestamp)}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

function Dashboard() {
	const [stats, setStats] = useState<DashboardStats | null>(null);
	const [loading, setLoading] = useState(true);
	const user = getUser();
	const userName = user?.name?.split(' ')[0] || 'Usuario';

	useEffect(() => {
		const loadStats = async () => {
			setLoading(true);
			try {
				const data = await fetchDashboardStats();
				setStats(data);
			} catch (error: unknown) {
				toasterNotification({
					msg: extractErrorMessage(error) || 'Error al cargar el dashboard',
					toasterType: toasterOptions.error,
				});
			} finally {
				setLoading(false);
			}
		};

		loadStats();
	}, []);

	if (loading) {
		return (
			<div className="dashboard_page">
				<div className="dashboard_loading">
					<Spinner />
				</div>
			</div>
		);
	}

	if (!stats) {
		return (
			<div className="dashboard_page">
				<div className="dashboard_topbar">
					<div className="dashboard_header_text">
						<div className="dashboard_page_title">
							{getGreeting()}, {userName}
						</div>
						<div className="dashboard_page_subtitle">{formatFullDate()}</div>
					</div>
				</div>
				<div className="dashboard_content">
					<p>No se pudieron cargar las métricas</p>
				</div>
			</div>
		);
	}

	const sparklines = stats.kpiSparklines ?? {
		totalTagsInstalled: Array(7).fill(0),
		totalTagsDelivered: Array(7).fill(0),
		averageInstallationsPerMonth: Array(7).fill(0),
		totalConversations: Array(7).fill(0),
		activeGeofences: Array(7).fill(0),
		inactiveVehicles: Array(7).fill(0),
	};

	const dailySeries =
		stats.dailySeries?.length > 0
			? stats.dailySeries
			: Array.from({ length: 30 }, (_, i) => {
					const d = new Date();
					d.setDate(d.getDate() - (29 - i));
					return {
						date: d.toISOString().split('T')[0],
						installed: 0,
						delivered: 0,
					};
			  });

	const kpiCards: KpiCardConfig[] = [
		{
			title: 'Total Tags instalados',
			value: stats.totalTagsInstalled,
			subtitle: 'Vehículos activos con tag asignado',
			tooltip: METRIC_TOOLTIPS.totalTagsInstalled,
			icon: <MemoryOutlinedIcon fontSize="small" />,
			color: 'orange',
			sparklineKey: 'totalTagsInstalled',
		},
		{
			title: 'Total Tags entregados',
			value: stats.totalTagsDelivered,
			subtitle: 'Tags registrados en la compañía',
			tooltip: METRIC_TOOLTIPS.totalTagsDelivered,
			icon: <Inventory2OutlinedIcon fontSize="small" />,
			color: 'blue',
			sparklineKey: 'totalTagsDelivered',
		},
		{
			title: 'Promedio de instalaciones al mes',
			value: stats.averageInstallationsPerMonth,
			tooltip: METRIC_TOOLTIPS.averageInstallationsPerMonth,
			icon: <TrendingUpOutlinedIcon fontSize="small" />,
			color: 'green',
			sparklineKey: 'averageInstallationsPerMonth',
		},
		{
			title: 'Conversaciones con SauronAI',
			value: stats.totalConversations,
			tooltip: METRIC_TOOLTIPS.totalConversations,
			icon: <ChatBubbleOutlineOutlinedIcon fontSize="small" />,
			color: 'amber',
			sparklineKey: 'totalConversations',
		},
		{
			title: 'Geocercas activas',
			value: stats.activeGeofences,
			tooltip: METRIC_TOOLTIPS.activeGeofences,
			icon: <MapOutlinedIcon fontSize="small" />,
			color: 'purple',
			sparklineKey: 'activeGeofences',
		},
		{
			title: 'Vehículos desactivados',
			value: stats.inactiveVehicles,
			tooltip: METRIC_TOOLTIPS.inactiveVehicles,
			icon: <DirectionsCarFilledOutlinedIcon fontSize="small" />,
			color: 'red',
			sparklineKey: 'inactiveVehicles',
		},
	];

	return (
		<div className="dashboard_page">
			<div className="dashboard_topbar">
				<div className="dashboard_header_text">
					<div className="dashboard_page_title">
						{getGreeting()}, {userName}
					</div>
					<div className="dashboard_page_subtitle">{formatFullDate()}</div>
				</div>
			</div>

			<div className="dashboard_content">
				<div className="dashboard_section_label">Resumen general</div>
				<div className="dashboard_kpi_grid">
					{kpiCards.map(
						({
							sparklineKey,
							title,
							value,
							subtitle,
							tooltip,
							icon,
							color,
						}) => (
							<KpiCard
								key={title}
								title={title}
								value={value}
								subtitle={subtitle}
								tooltip={tooltip}
								icon={icon}
								color={color}
								sparklineData={sparklines[sparklineKey]}
							/>
						),
					)}
				</div>

				<div className="dashboard_section_label">Actividad por periodo</div>
				<PeriodTable
					installed={stats.tagsInstalledByPeriod}
					delivered={stats.tagsDeliveredByPeriod}
				/>

				<div className="dashboard_section_label">Instalaciones por periodo</div>
				<div className="dashboard_chart_activity_grid">
					<InstallationsChart data={dailySeries} />
					<ActivityPanel activities={stats.recentActivity ?? []} />
				</div>
			</div>
		</div>
	);
}

export default Dashboard;
